// セクション描画用の高さ計算・合計表示補助をまとめる service です。
export function createSectionDisplayHelperService(deps) {
    const {
        GridData,
        normalizeTotalTypeForColumns,
        toStableAutoHeightPanel,
        isEmpty,
        DicVal,
        colorwayMatrixMap,
        warndescriptionRef
    } = deps;

    // 登録時警告が指している明細行を panel 単位で集めます。
    function warningRowIdSet(panelInfo) {
        const list = warndescriptionRef?.current;
        if (!Array.isArray(list) || list.length === 0) return null;
        const panelType = String(DicVal(panelInfo, "type", ""));
        if (panelType === "") return null;

        const rowids = new Set();
        list.forEach((warn) => {
            const rowid = String(DicVal(warn, "rowid", ""));
            // rowid 空は単票項目の警告。明細行の着色対象にはしません。
            if (rowid === "") return;
            // rowid は grid 間で重複するため、必ず panel type で絞り込みます。
            if (String(DicVal(warn, "type", "")) !== panelType) return;
            rowids.add(rowid);
        });
        return rowids.size === 0 ? null : rowids;
    }

    // 対象グリッドの行数を安全に取得します。
    function gridRowCount(panelInfo) {
        const rows = GridData(panelInfo);
        return Array.isArray(rows) ? rows.length : 0;
    }

    // 受注数/納品予定グリッドの固定高さを行数から計算します。
    function demandGridFixedHeight(panelInfo) {
        const rowCount = gridRowCount(panelInfo);
        const calc = (rowCount * 34) + 180;
        const h = Math.max(760, calc);
        return h + "px";
    }

    // 指示数グリッドはコンパクトに表示し、合計をすぐ下へ寄せます。
    function sijisuGridFixedHeight(panelInfo) {
        const rowCount = gridRowCount(panelInfo);
        const calc = (rowCount * 34) + 86;
        const h = Math.max(120, calc);
        return h + "px";
    }

    // 通常グリッドは行数に応じて高さを決めます。
    function basicGridFixedHeight(panelInfo) {
        const rowCount = gridRowCount(panelInfo);
        const calc = (rowCount * 34) + 86;
        const h = Math.max(120, Math.min(420, calc));
        return h + "px";
    }

    // 原価左グリッドは全行表示を優先し内部スクロールを出しません。
    function genkaGridFixedHeight(panelInfo) {
        const rowCount = gridRowCount(panelInfo);
        const calc = (rowCount * 28) + 64;
        const h = Math.max(460, calc);
        return h + "px";
    }

    // 合計エリアの見出し文字を返します。
    function totalTitle(section, sideSummary) {
        if (section.id === "genka") return "";
        if (sideSummary) return "合計";
        return "";
    }

    // 合計エリアの配置スタイルを返します。
    function totalContainerStyle(section, sideSummary) {
        if (sideSummary) return { minWidth: 280, width: 280, marginLeft: -1 };
        return { marginTop: 8 };
    }

    // 合計表示用 panel を構築します。
    function buildTotalPanel(section, gridPanel, totaltype) {
        const resolvedTotalType = normalizeTotalTypeForColumns(totaltype, section.id);
        const totalPanel = {
            ...toStableAutoHeightPanel(gridPanel, section.id),
            totaltype: resolvedTotalType,
            ...(section.id === "genka" ? { gridmax: "99999" } : {})
        };
        return { totalPanel, resolvedTotalType };
    }

    // 受注数/納品予定セクションかを判定します。
    function isDemandSectionId(sectionId) {
        return sectionId === "jutyusu" || sectionId === "nouhinyotei";
    }

    // 受注数/納品予定の集計行スタイルと、登録時警告行の強調を返します。
    function sectionGridRowStyle(sectionId, panelInfo) {
        const isDemandSection = sectionId === "jutyusu" || sectionId === "nouhinyotei";
        const warningRowIds = warningRowIdSet(panelInfo);
        if (!isDemandSection && warningRowIds === null) return null;

        return function (params) {
            if (isEmpty(params) || isEmpty(params.data)) return null;
            // 警告行の強調は集計行スタイルより優先します。
            if (warningRowIds !== null && warningRowIds.has(String(DicVal(params.data, "rowid", "")))) {
                return { backgroundColor: "#fdecea" };
            }
            if (!isDemandSection) return null;

            const kind = String(params.data.row_kind || "");
            if (sectionId === "jutyusu") {
                if (kind === "subtotal") return { backgroundColor: "#cfe0e0", fontWeight: 700 };
                if (kind === "total") return { backgroundColor: "#c5d9d9", fontWeight: 700 };
                return null;
            }
            if (kind === "summary_label") return { backgroundColor: "#a7bcbc", color: "#ffffff", fontWeight: 700 };
            if (kind === "summary_total") return { backgroundColor: "#eef1f1", color: "#333", fontWeight: 500 };
            if (kind === "subtotal") return { backgroundColor: "#d6e3e3", fontWeight: 600 };
            if (kind === "total") return { backgroundColor: "#c7dbdb", fontWeight: 700 };
            return null;
        };
    }

    // 素材配色/附属配色パネルかを判定します。
    function isColorwayPanel(panelInfo) {
        if (isEmpty(panelInfo)) return false;
        return panelInfo.id === "3"
            || panelInfo.id === "5"
            || panelInfo.id === "sozai_haisyoku"
            || panelInfo.id === "fuzoku_haisyoku"
            || panelInfo.type === "108"
            || panelInfo.type === "111"
            || panelInfo.type === "sozai_haisyoku"
            || panelInfo.type === "fuzoku_haisyoku";
    }

    // 素材配色/附属配色のマスタ詳細設定を作成します。
    function colorwayDetailParams(sectionId) {
        const matrix = colorwayMatrixMap[sectionId] || { size: [], rows: [] };
        const sizeColumns = Array.isArray(matrix.size) ? matrix.size : [];
        return {
            detailGridOptions: {
                columnDefs: [
                    { headerName: "C/#", field: "colno", width: 90 },
                    { headerName: "カラー", field: "colnm", width: 130 },
                    ...sizeColumns
                        .filter((size) => !isEmpty(DicVal(size, "field", "")))
                        .map((size) => ({
                            headerName: String(DicVal(size, "sizeNm", DicVal(size, "sizeCd", ""))),
                            field: String(DicVal(size, "field", "")),
                            width: 90,
                            editable: false
                        }))
                ],
                defaultColDef: {
                    sortable: false,
                    filter: false,
                    resizable: true,
                    editable: false
                },
                rowSelection: "single",
                suppressRowClickSelection: true,
                enableRangeSelection: false
            },
            getDetailRowData: (params) => {
                const rows = Array.isArray(params?.data?.callRecords) ? params.data.callRecords : [];
                params.successCallback(rows);
            }
        };
    }

    return {
        demandGridFixedHeight,
        sijisuGridFixedHeight,
        basicGridFixedHeight,
        genkaGridFixedHeight,
        totalTitle,
        totalContainerStyle,
        buildTotalPanel,
        isDemandSectionId,
        sectionGridRowStyle,
        isColorwayPanel,
        colorwayDetailParams
    };
}
